import { type ReactElement } from "react";
import type { LucideIcon, LucideProps } from "lucide-react";
import { cn } from "@/lib/utils";
import { getColorClasses } from "@/lib/colors";
import { ICON_REGISTRY, resolveIcon, type IconName } from "./icon-registry";

type IconSize = "sm" | "md" | "lg";

const sizeClasses: Record<IconSize, { container: string; icon: string }> = {
    sm: { container: "h-6 w-6", icon: "h-3.5 w-3.5" },
    md: { container: "h-8 w-8", icon: "h-4 w-4" },
    lg: { container: "h-10 w-10", icon: "h-5 w-5" },
};

interface EntityIconProps {
    name: IconName | string;
    /** Render inside a colored rounded badge instead of as a bare glyph. */
    badge?: boolean;
    size?: IconSize;
    /** Semantic color token (e.g. "palette-1", "neutral", "info"). Only used when `badge` is true. */
    color?: string;
    className?: string;
    title?: string;
    "aria-label"?: string;
}

/**
 * Renders a centrally-registered entity / attribute icon.
 *
 * Two modes:
 *  - default: bare lucide glyph, sized by `size`. Color follows currentColor.
 *  - badge:   icon centered in a rounded square tinted with a palette color.
 *
 * Examples:
 *   <EntityIcon name="vehicle" />
 *   <EntityIcon name="claim" badge color="palette-9" size="md" />
 */
export function EntityIcon({
    name,
    badge = false,
    size = "md",
    color = "neutral",
    className,
    title,
    "aria-label": ariaLabel,
}: EntityIconProps): ReactElement | null {
    const Icon = resolveIcon(name);
    if (!Icon) return null;

    const s = sizeClasses[size];
    const label = ariaLabel ?? title;

    if (!badge) {
        return (
            <Icon
                className={cn(s.icon, className)}
                aria-label={label}
                aria-hidden={label ? undefined : true}
            />
        );
    }

    const c = getColorClasses(color);
    return (
        <div
            className={cn(
                "flex shrink-0 items-center justify-center rounded-lg",
                s.container,
                c.bg,
                className,
            )}
            title={title}
            role={label ? "img" : undefined}
            aria-label={label}
        >
            <Icon className={cn(s.icon, c.icon)} aria-hidden />
        </div>
    );
}

/**
 * Resolve the raw lucide component for a registered name. Use this when you
 * need to pass the icon down a prop (e.g. into a Button leftIcon slot).
 *
 * Prefer `<EntityIcon />` for direct rendering.
 */
export function getEntityIcon(name: IconName | string): LucideIcon | null {
    return resolveIcon(name);
}

export type { IconName, LucideProps };
export { ICON_REGISTRY };
